<template>
  <Panel
    :id="id"
    class="line-item-edit elevated"
    :aria-invalid="!!errorResponse ? 'true' : 'false'"
    tabindex="-1"
  >
    <template #header>
      <div class="line-item-edit-header">
        <h2>{{ t('lineItems.lineItems') }}</h2>

        <div class="line-item-edit-header-right">
          <div class="line-item-edit-header-description">
            <span class="line-item-description-text">
              {{ formattedDescription }}
            </span>
            <span class="font-normal flex-shrink-0">
              {{ formattedHeaderDims }}
            </span>
          </div>
        </div>
      </div>
    </template>

    <template #icons>
      <RygenButton
        v-if="removable"
        mode="tertiary"
        :aria-label="
          t('common.removeParam', {
            param: t('common.paramNum', {
              param: t('lineItems.lineItems'),
              num: index + 1,
            }),
          })
        "
        :label="t('common.remove')"
        :severity="Severity.DANGER"
        :data-testid="`${id}-remove-button`"
        icon="pi pi-trash"
        @click="emit('remove', lineItem)"
      />

      <RygenButton
        v-if="lineItem.productId || v$.$anyDirty"
        mode="tertiary"
        :label="t('orders.clearAndStartOver')"
        :data-testid="`${id}-clear-button`"
        @click="onClear"
      />

      <RygenButton
        mode="tertiary"
        :aria-label="
          t('common.duplicateParam', {
            param: t('common.paramNum', {
              param: t('lineItems.lineItems'),
              num: index + 1,
            }),
          })
        "
        :label="t('common.duplicate')"
        :data-testid="`${id}-duplicate-button`"
        icon="pi pi-copy"
        @click="emit('duplicate', lineItem)"
      />

      <ExpandButton
        v-model="isExpanded"
        :target-id="`${id}-content`"
        :data-testid="`${id}-expand-button`"
        no-labels
      />
    </template>

    <template #default>
      <LineItemEditContent
        :id="`${id}-content`"
        v-model="lineItem"
        v-model:expanded="isExpanded"
        :index="index"
        :handling-units="handlingUnits"
        :error-response="errorResponse"
        :removable="removable"
        :show-stops="showStops"
        :show-save-product-switch="showSaveProductSwitch"
        :disable-default-reference-numbers="disableDefaultReferenceNumbers"
        class="line-item-edit-content"
        @clear="onClear"
        @remove="emit('remove', $event)"
      />
    </template>
  </Panel>
</template>

<script lang="ts" setup>
import axios from 'axios';
import isEqual from 'lodash/isEqual';
import type { PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useValidations } from '@rygen/rygen-ui/composables';
import {
  ErrorResponse,
  HandlingUnit,
  Loadable,
  Severity,
} from '@rygen/rygen-ui/model';

import api from '@/api';
import { DoesUserHavePermissionEntityType, LineItem } from '@/model';

import LineItemEditContent from '@/components/lineItem/LineItemEditContent.vue';
import ExpandButton from '@rygen/rygen-ui/components/button/ExpandButton.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Panel from 'primevue/panel';

const isExpanded = defineModel('expanded', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  modelValue: {
    type: LineItem,
    default: () => new LineItem(),
  },
  index: {
    type: Number,
    default: () => 1,
  },
  handlingUnits: {
    type: Object as PropType<Loadable<HandlingUnit[]>>,
    default: () => ({ loading: false }),
  },
  errorResponse: {
    type: Object as PropType<ErrorResponse>,
    default: () => undefined,
  },
  removable: {
    type: Boolean,
    default: () => false,
  },
  showStops: {
    type: Boolean,
    default: () => false,
  },
  disableDefaultReferenceNumbers: {
    type: Boolean,
    default: () => false,
  },
  enableSaveProductSwitch: {
    type: Boolean,
    default: () => false,
  },
});
const {
  modelValue,
  index,
  handlingUnits,
  errorResponse,
  removable,
  showStops,
  disableDefaultReferenceNumbers,
  enableSaveProductSwitch,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'duplicate', lineItem: LineItem): void;
  (event: 'remove', lineItem: LineItem): void;
  (event: 'update:model-value', newVal: LineItem): void;
}>();

const Clazz = LineItem.getSubclass(modelValue.value);
const lineItem: Ref<LineItem> = ref(new Clazz(modelValue.value));

const { n, t } = useI18n();
const { v$ } = useValidations(lineItem, {});

const doesUserHaveUpdateProductPermission = ref(false);

const hasProductCreate = computed(() =>
  authorization.hasAuthority('product', 'create'),
);

const isFiconProduct = computed(() => !!lineItem.value.productId);

const showSaveProductSwitch = computed(() => {
  const hasProductPermission = isFiconProduct.value
    ? doesUserHaveUpdateProductPermission.value
    : hasProductCreate.value;

  return hasProductPermission && enableSaveProductSwitch.value;
});

const id = computed(() => `line-item-num-${index.value + 1}`);

const formattedDescription = computed(() => {
  return (
    lineItem.value.description ??
    t('common.noParam', { param: t('lineItems.lineItems') })
  );
});

const formattedHandling = computed(() => {
  const total = n(lineItem.value.quantity ?? 0, 'basic');
  return [total, lineItem.value.handlingUnit?.name].join(' ');
});

const formattedDims = computed(() => {
  const length = lineItem.value.lengthDim ?? 0;
  const width = lineItem.value.widthDim ?? 0;
  const height = lineItem.value.heightDim ?? 0;
  const uom = t(`uoms.length.abbreviations.${lineItem.value.dimensionalUom}`);

  return `${length} x ${width} x ${height} ${uom}`;
});

const formattedHeaderDims = computed(
  () => `, ${[formattedHandling.value, formattedDims.value].join(', ')}`,
);

const onClear = () => {
  lineItem.value = new Clazz();
  nextTick(() => v$.value.$reset());
};

const loadUpdateProductPermission = async (productId: number) => {
  try {
    doesUserHaveUpdateProductPermission.value =
      await api.user.doesCurrentUserHavePermission(
        DoesUserHavePermissionEntityType.PRODUCT,
        productId,
        'product:update',
        true,
      );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }
    console.error('Error loading product permission', e);
  }
};

watch(
  () => lineItem.value.productId,
  (newVal) => {
    doesUserHaveUpdateProductPermission.value = false;

    if (newVal) {
      loadUpdateProductPermission(newVal);
    }
  },
  { immediate: true },
);

watch(
  modelValue,
  (newVal: LineItem) => {
    lineItem.value = new Clazz(newVal);
  },
  { immediate: true, deep: true },
);

watch(
  lineItem,
  (newVal: LineItem) => {
    if (!isEqual(newVal, modelValue.value)) {
      emit('update:model-value', newVal);
    }
  },
  { deep: true },
);
</script>

<style>
:root {
  --line-item-edit-border-color: var(--p-blue-500);
}

.line-item-edit.p-panel {
  --p-panel-border-radius: 0 var(--p-border-radius-sm) var(--p-border-radius-sm)
    var(--p-border-radius-sm);
  --p-panel-header-background: white;
  --p-panel-header-border-width: 0 0 1px 0;
  --p-panel-header-border-color: transparent transparent var(--divider-color)
    transparent;
  --p-panel-header-border-radius: 0 var(--p-border-radius-sm) 0 0;
  --p-panel-header-color: var(--p-gray-600);
  --p-panel-header-padding: 0 var(--sp-6) 0 0;
  --p-panel-content-padding: var(--sp-3) 0;

  counter-increment: line-item-num;
  border: none;
  border-left: var(--sp-1) solid var(--line-item-edit-border-color);
  position: relative;
  z-index: 2;

  &::before {
    content: counter(line-item-num);
    position: absolute;
    right: 100%;
    background-color: var(--line-item-edit-border-color);
    color: white;
    padding: var(--sp-2);
    border-radius: var(--p-border-radius-sm) 0 0 var(--p-border-radius-sm);
    font-weight: 700;
  }

  .p-panel-header {
    font-size: 16px;
    font-weight: 500;

    .line-item-edit-header {
      display: flex;
      flex: 1 0 auto;

      h2 {
        border-right: var(--divider);
        padding: var(--sp-3) var(--sp-3) var(--sp-3) var(--sp-6);
        width: 200px;
      }

      .line-item-edit-header-right {
        display: flex;
        gap: var(--gap-1);
        justify-content: space-between;
        align-items: center;
        width: 100%;
        padding: var(--sp-3);

        .line-item-edit-header-description {
          display: flex;
          max-width: var(--panel-header-text-max-width);
          overflow: hidden;
          gap: var(--gap-1);
          align-items: center;

          .line-item-description-text {
            font-weight: normal;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            min-width: 0;
          }
        }
      }
    }
  }

  .p-panel-content {
    .line-item-edit-content {
      width: 100%;
    }
  }
}
</style>
